import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import { OPTION_ITEM_CONFIG } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/consts';
import {
  renderAttachments,
  renderKeyCode,
  renderOptionStatus,
  renderOptionText,
} from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/renderers';
import { OptionsListItemProps } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/types';

function OptionsListItem(props: OptionsListItemProps) {
  const { t } = useTranslation();
  const { index, option, onChange, type, isQuestionSubmitted, keyCode } = props;
  const { labelKey, classes, renderKeyCodeContent, dataTestId } = OPTION_ITEM_CONFIG[type];

  const onClick = useCallback(() => {
    if (isQuestionSubmitted) {
      return;
    }
    onChange(option);
  }, [isQuestionSubmitted, onChange, option]);

  useHotkeys(`${index + 1}`, onClick, [onClick, index, keyCode]);

  return (
    <Stack direction="col" role={isQuestionSubmitted ? '' : 'button'} onClick={onClick}>
      {renderAttachments({ attachments: option.attachments, className: classes.attachments })}
      <Stack
        className={cn(classes.listItem, { 'pointer-events-none': isQuestionSubmitted })}
        data-test-id={isQuestionSubmitted ? dataTestId : undefined}
        gapX={8}>
        {renderKeyCode({ className: classes.keyCode, children: renderKeyCodeContent(keyCode) })}
        <Box className="min-w-0 flex-1">
          {renderOptionText({
            optionText: option.optionText,
            className: classes.optionText,
            secondaryBackgroundColor: classes.secondaryBackgroundColor,
          })}
        </Box>
        {isQuestionSubmitted &&
          renderOptionStatus({
            t,
            labelKey,
            className: classes.status,
            params: { percentage: option.optionRelativeScore ?? 0 },
          })}
      </Stack>
    </Stack>
  );
}

export default memo(OptionsListItem);
